import { createBigDecimal, minus, plus, times } from '@0xtorch/big-decimal'
import type { Nft, Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type { Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import type {
  CreateCategoryTransactionParameters,
  CreateCategoryTransactionReturnTypes,
} from './types'
import { addBalanceToAssetBorrowsAndAssetBalances } from './util/addBalanceToAssetBorrowsAndAssetBalances'

type CreateValueDownTransactionParameters =
  CreateCategoryTransactionParameters & {
    readonly target: Nft
    readonly transfer: Transfer
    readonly costBasis: CostBasis
  }

type CreateValueDownTransactionReturnTypes =
  CreateCategoryTransactionReturnTypes

export const createValueDownTransaction = ({
  actionId,
  timestamp,
  target,
  transfer,
  assetBalances,
  assetBorrows,
  usedBalances,
  costBasis,
}: CreateValueDownTransactionParameters): CreateValueDownTransactionReturnTypes => {
  const key = createAssetKey(transfer.asset)
  const price = transfer.price ?? createBigDecimal(0n)
  const value = times(transfer.amount, price, 6)

  // Add balance
  const { assetBalances: keyAssetBalances, assetBorrows: keyAssetBorrows } =
    transfer.asset.type === 'FiatCurrency'
      ? {
          assetBalances: [],
          assetBorrows: [],
        }
      : addBalanceToAssetBorrowsAndAssetBalances({
          amount: transfer.amount,
          value,
          assetBalances: assetBalances.get(key) ?? [],
          assetBorrows: assetBorrows.get(key) ?? [],
          costBasis,
        })
  if (keyAssetBalances.length > 0) {
    assetBalances.set(key, keyAssetBalances)
  } else {
    assetBalances.delete(key)
  }
  if (keyAssetBorrows.length > 0) {
    assetBorrows.set(key, keyAssetBorrows)
  } else {
    assetBorrows.delete(key)
  }

  // Reduce target balance value
  const targetKey = createAssetKey(target)
  const targetAssetBalances = assetBalances.get(targetKey) ?? []
  if (targetAssetBalances.length === 0) {
    throw new Error('Target asset balance not found')
  }
  targetAssetBalances[0].value = minus(targetAssetBalances[0].value, value)
  assetBalances.set(targetKey, targetAssetBalances)

  const balance = (assetBalances.get(key) ?? []).reduce(
    (acc, assetBalance) => plus(acc, assetBalance.amount),
    createBigDecimal(0n),
  )
  const borrowing = (assetBorrows.get(key) ?? []).reduce(
    (acc, assetBorrow) => plus(acc, assetBorrow.amount),
    createBigDecimal(0n),
  )

  const transaction: Transaction = {
    actionId,
    order: 0,
    timestamp,
    category: 'value-down',
    ins: [
      {
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost: value,
        balance,
        borrowing,
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      },
    ],
    outs: [],
  }

  return {
    transaction,
    assetBalances,
    assetBorrows,
    usedBalances,
  }
}
